import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext, supabase } from '../../context/AuthContext';
import logger from '../../utils/logger';
import './Agents.css';

const AgentStats = () => {
  const { agentId } = useParams();
  const { session } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!agentId || !session) return;
      
      setIsLoading(true);
      try {
        // Convert agentId to number for Supabase query
        // This is important because the stats table has project_id as int4 type
        const projectIdNum = parseInt(agentId, 10);
        if (isNaN(projectIdNum)) {
          throw new Error('Invalid agent ID format');
        }
        
        logger.debug('Fetching stats for project ID:', projectIdNum);
        
        // First attempt to get stats from our database
        const { data: statsData, error: statsError } = await supabase
          .from('stats')
          .select('*')
          .eq('project_id', projectIdNum)
          .maybeSingle();
        
        if (statsError) {
          logger.error('Error fetching stats from Supabase:', statsError);
        }
        
        // If we have stats in our database, use them
        if (statsData) {
          logger.debug('Stats found in Supabase:', statsData);
          setStats(statsData);
          setIsLoading(false);
          return;
        }
        
        // Otherwise fetch from the API
        const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
        logger.debug(`Fetching stats from API: ${baseUrl}/api/agent/${agentId}/stats`);
        
        const response = await fetch(`${baseUrl}/api/agent/${agentId}/stats`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        logger.debug('Stats data from API:', data);
        setStats(data.data);
        
      } catch (err) {
        logger.error('Error in stats fetching process:', err);
        setError('Failed to load statistics');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, [agentId, session]);

  // Format large numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  if (isLoading) {
    return <div className="loading-indicator">Loading statistics...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!stats) {
    return <div className="no-data-message">No statistics available for this agent.</div>;
  }

  return (
    <div className="agent-stats-container">
      <h2>Agent Statistics</h2>
      
      <div className="stats-cards">
        <div className="stats-card">
          <h3>Pages Found</h3>
          <div className="stat-value">{formatNumber(stats.pages_found || 0)}</div>
        </div>
        
        <div className="stats-card">
          <h3>Pages Crawled</h3>
          <div className="stat-value">{formatNumber(stats.pages_crawled || 0)}</div>
        </div>
        
        <div className="stats-card">
          <h3>Pages Indexed</h3>
          <div className="stat-value">{formatNumber(stats.pages_indexed || 0)}</div>
        </div>
        
        <div className="stats-card">
          <h3>Total Queries</h3>
          <div className="stat-value">{formatNumber(stats.total_queries || 0)}</div>
        </div>
      </div>
      
      <div className="stats-section">
        <h3>Usage Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Crawl Credits Used:</span>
            <span className="stat-value">{formatNumber(stats.crawl_credits_used || 0)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Query Credits Used:</span>
            <span className="stat-value">{formatNumber(stats.query_credits_used || 0)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Words Indexed:</span>
            <span className="stat-value">{formatNumber(stats.total_words_indexed || 0)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Storage Credits Used:</span>
            <span className="stat-value">{formatNumber(stats.total_storage_credits_used || 0)}</span>
          </div>
        </div>
      </div>
      
      <div className="stats-section">
        <h3>Timeline</h3>
        <div className="stats-timeline">
          <div className="timeline-item">
            <span className="timeline-label">Created:</span>
            <span className="timeline-value">{stats.created_at ? new Date(stats.created_at).toLocaleString() : 'N/A'}</span>
          </div>
          <div className="timeline-item">
            <span className="timeline-label">Last Updated:</span>
            <span className="timeline-value">{stats.updated_at ? new Date(stats.updated_at).toLocaleString() : 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentStats;
